import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { CarTestService } from "@/services/carTest/carTestService";
import { getLanguage, type Language } from "@/utils/languageUtils";
import { listingPostLabels } from "@/i18n";
import { isIOS, isAndroid } from "@/utils/platformUtils";
import type {
  VehicleTestImages,
  ExteriorImage,
  DamageImage,
  ServiceDetail,
} from "@/types/vehicleTest";
import { FileObject } from "@/types/shared/file";
import { AddedImage } from "@/selectors/vehicleTestSelectors";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  pickNativeCameraPhoto,
  pickNativeGalleryPhoto,
} from "@/lib/native/nativeImagePicker";
import { compressImage } from "@/utils/imageCompression";
import { EXTERIOR_MAX_REGULAR } from "@/config/constants";
import {
  ENGINE_VIDEO_COMPRESS_URL,
  ENGINE_VIDEO_CONVERT_URL,
  ENGINE_VIDEO_MAX_MB,
  ENGINE_VIDEO_MAX_SECONDS,
  isVideoFile,
  validateEngineVideo,
} from "@/lib/engineVideo";
import { isVideoMedia } from "@/lib/isVideoMedia";

interface UseImageUploadProps {
  language?: Language;
  currentImages: VehicleTestImages;
  setCurrentImages: React.Dispatch<React.SetStateAction<VehicleTestImages>>;
  setAddedImages: React.Dispatch<React.SetStateAction<AddedImage[]>>;
  setRemovedImages: React.Dispatch<React.SetStateAction<string[]>>;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  setExpandedComments: React.Dispatch<
    React.SetStateAction<{ [key: number]: boolean }>
  >;
  uploadingImages: Set<string>;
  setUploadingImages: React.Dispatch<React.SetStateAction<Set<string>>>;
  setUploadingThumbnail: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadingRegularExterior: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadingEngineVideo: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode: boolean;
  testId?: string;
  processedImagesRef: React.MutableRefObject<Set<string>>;
}

export const useImageUpload = ({
  language,
  currentImages,
  setCurrentImages,
  setAddedImages,
  setRemovedImages,
  setHasChanges,
  setExpandedComments,
  uploadingImages,
  setUploadingImages,
  setUploadingThumbnail,
  setUploadingRegularExterior,
  setUploadingEngineVideo,
  isEditMode,
  testId,
  processedImagesRef,
}: UseImageUploadProps) => {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const lang = language ?? getLanguage();

  const processImageFile = useCallback(
    async (
      file: File,
      section: keyof VehicleTestImages,
      isThumbnail: boolean = false,
      tireType?: "primary" | "secondary",
    ): Promise<string> => {
      const fileIsVideo = isVideoFile(file);
      // Motor video must never be the listing thumbnail.
      if (fileIsVideo) {
        isThumbnail = false;
      }

      // Engine video clip (exterior only): validate duration/size, never compress as image.
      if (fileIsVideo) {
        if (section !== "exterior") {
          const msg = "Video är bara tillåtet bland exteriörbilder.";
          toast.error(msg);
          throw new Error(msg);
        }
        const ev =
          (
            listingPostLabels(lang).images as {
              engineVideo?: Record<string, string>;
            }
          )?.engineVideo ?? {};
        const validation = await validateEngineVideo(file);
        if (validation.ok === false) {
          const { reason } = validation as { ok: false; reason: string };
          let msg: string;
          if (reason === "too_large") {
            msg = (ev.tooLarge ?? "Videon är för stor. Max {mb} MB.").replace(
              "{mb}",
              String(ENGINE_VIDEO_MAX_MB),
            );
          } else if (reason === "too_long") {
            msg = (
              ev.tooLong ?? "Videon är för lång. Max {max} sekunder."
            ).replace("{max}", String(ENGINE_VIDEO_MAX_SECONDS));
          } else if (reason === "wrong_format") {
            msg =
              ev.wrongFormat ??
              "Motorvideo måste vara MP4 eller MOV (inte WebM).";
          } else {
            msg = ev.unreadable ?? "Videon kunde inte läsas.";
          }
          const actionUrl =
            reason === "wrong_format"
              ? ENGINE_VIDEO_CONVERT_URL
              : ENGINE_VIDEO_COMPRESS_URL;
          const actionLabel =
            reason === "wrong_format"
              ? (ev.convertLink ?? "Konvertera till MP4")
              : (ev.compressLink ?? "Komprimera video");
          toast.error(msg, {
            action: {
              label: actionLabel,
              onClick: () =>
                window.open(actionUrl, "_blank", "noopener,noreferrer"),
            },
            duration: 10000,
          });
          // Throw (not silent return) so the photo-session queue marks it failed.
          throw new Error(msg);
        }
      }

      // Validate file type and size BEFORE processing
      const { SUPPORTED_IMAGE_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } =
        await import("@/config/constants");
      if (!fileIsVideo && !SUPPORTED_IMAGE_TYPES.includes(file.type)) {
        const allowedExtensions = SUPPORTED_IMAGE_TYPES.map((t) => {
          const ext = t.split("/")[1].toUpperCase();
          return ext === "JPEG" || ext === "JPG" ? "JPEG" : ext;
        }).filter((v, i, a) => a.indexOf(v) === i);
        const toasts =
          (
            listingPostLabels(lang).images as {
              toasts?: Record<string, string>;
            }
          )?.toasts ?? {};
        const invalidMsg = (
          toasts.invalidFileType ??
          "Ogiltigt filformat. Endast {extensions} är tillåtna."
        ).replace("{extensions}", allowedExtensions.join(", "));
        toast.error(invalidMsg);
        return;
      }

      if (!fileIsVideo && file.size > MAX_FILE_SIZE_BYTES) {
        const toasts =
          (
            listingPostLabels(lang).images as {
              toasts?: Record<string, string>;
            }
          )?.toasts ?? {};
        const msg = (
          toasts.fileTooLarge ?? "Filen är för stor. Max storlek är {mb}MB."
        ).replace("{mb}", String(MAX_FILE_SIZE_MB));
        toast.error(msg);
        return;
      }
      // Save original file BEFORE compression - we'll use this for full-size display in popup
      const originalFile = file;

      // Compress image BEFORE processing - this reduces file size and upload time
      // Target: max 700KB (0.7 MB) per image
      // Same compression for all images (thumbnails and regular)
      let compressedFile: File;
      if (!fileIsVideo) {
        try {
          const MAX_SIZE_MB = 0.7; // 700KB maximum file size
          compressedFile = await compressImage(file, {
            maxSizeMB: MAX_SIZE_MB,
          });

          // Update file to use compressed version for all subsequent operations
          file = compressedFile;

          // Re-validate size after compression (should be fine, but just in case)
          if (file.size > MAX_FILE_SIZE_BYTES) {
          }
        } catch (compressionError) {
          // Continue with original file if compression fails (graceful degradation)
          // Don't show error to user unless compression was critical
        }
      }

      const tempId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 11)}`;

      const fileExtension = file.name.split(".").pop() || "";
      const guid = crypto.randomUUID();
      const newFileName = `${guid}.${fileExtension}`;
      file = new File([file], newFileName, {
        type: file.type,
        lastModified: file.lastModified,
      });

      // Diagnostic: Log file info at start
      const fileStartTime = Date.now();

      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        let isResolved = false;
        let isRejected = false;
        let readerTimeout: NodeJS.Timeout | null = null;

        // Helper function to add image to state
        const addImageToState = (
          newImage: any,
          imageData: AddedImage,
          skipAddedImages = false,
        ) => {
          // Verify image has required fields before adding
          if (!newImage || !newImage.id || !newImage.url) {
            return;
          }

          // Use functional updates to ensure we have latest state and avoid race conditions
          if (section === "exterior") {
            setCurrentImages((prev) => {
              const exists = prev.exterior?.some(
                (img: any) => img.id === newImage.id,
              );
              if (exists) {
                return prev;
              }
              return {
                ...prev,
                exterior: [...(prev.exterior || []), newImage],
              };
            });
          } else if (section === "interior") {
            setCurrentImages((prev) => {
              const exists = prev.interior?.some(
                (img: any) => img.id === newImage.id,
              );
              if (exists) {
                return prev;
              }
              return {
                ...prev,
                interior: [...(prev.interior || []), newImage],
              };
            });
          } else if (section === "damage") {
            setCurrentImages((prev) => {
              const exists = prev.damage?.some(
                (img: any) => img.id === newImage.id,
              );
              if (exists) {
                return prev;
              }
              const newDamageArray = [...(prev.damage || []), newImage];
              const newIndex = newDamageArray.length - 1;
              setExpandedComments((prevComments) => ({
                ...prevComments,
                [newIndex]: true,
              }));
              return {
                ...prev,
                damage: newDamageArray,
              };
            });
          } else if (section === "serviceImages") {
            setCurrentImages((prev) => {
              const exists = prev.serviceImages?.some(
                (img: any) => img.id === newImage.id,
              );
              if (exists) {
                return prev;
              }
              return {
                ...prev,
                serviceImages: [...(prev.serviceImages || []), newImage],
              };
            });
          } else if (section === "tires") {
            const targetTireType = tireType || "primary";
            setCurrentImages((prev) => {
              const exists = prev.tires?.[targetTireType]?.some(
                (img: any) => img.id === newImage.id,
              );
              if (exists) {
                return prev;
              }
              return {
                ...prev,
                tires: {
                  ...prev.tires,
                  [targetTireType]: [
                    ...(prev.tires?.[targetTireType] || []),
                    newImage,
                  ],
                },
              };
            });
          }

          // Track added images - ALWAYS save originalFile for create mode (even for eager uploads)
          // This is needed so we can show full-size images in popup
          // In edit mode, we also need to track addedImages so hasChanges works correctly
          if (!skipAddedImages || (!isEditMode && imageData.originalFile)) {
            // Save originalFile for create mode (both normal flow and eager upload)
            // Also track in edit mode so hasChanges works correctly
            if (!isEditMode) {
              setAddedImages((prev) => {
                // For eager uploads, match by imageId (real ID) instead of file reference
                const exists = skipAddedImages
                  ? prev.some((added) => added.imageId === imageData.imageId)
                  : prev.some(
                      (added) =>
                        added.file === imageData.file &&
                        added.section === imageData.section &&
                        added.isThumbnail === imageData.isThumbnail &&
                        added.tireType === imageData.tireType,
                    );
                if (exists) {
                  return prev;
                }
                return [...prev, imageData];
              });
            } else if (isEditMode && !skipAddedImages) {
              // For edit mode, track in addedImages so hasChanges works correctly
              setAddedImages((prev) => {
                const exists = prev.some(
                  (added) =>
                    added.file === imageData.file &&
                    added.section === imageData.section &&
                    added.isThumbnail === imageData.isThumbnail &&
                    added.tireType === imageData.tireType,
                );
                if (exists) {
                  return prev;
                }
                return [...prev, imageData];
              });
            }
          } else {
            if (isEditMode) {
              setHasChanges(true);
            }
          }
        };

        // Helper function to start reading with proper timeout setup
        const startReading = () => {
          // Videos can be tens of MB – don't base64-read them (slow, hits timeout).
          // A blob: URL is enough for local preview; the File itself is uploaded.
          if (fileIsVideo && file.size > 0) {
            handleLoaded(URL.createObjectURL(file));
            return;
          }
          // Timeout for FileReader - longer for iOS camera files which can be slower
          // Set timeout only when we're actually ready to read
          const timeoutDuration = isIOS() ? 10000 : 5000; // 10 seconds for iOS, 5 for others
          const timeoutStartTime = Date.now();
          readerTimeout = setTimeout(() => {
            const timeoutElapsed = Date.now() - timeoutStartTime;
            if (!isResolved && !isRejected) {
              reader.abort();
              isRejected = true;
              reject(
                new Error(
                  "Timeout: Bilden kunde inte läsas in. Försök ta om fotot eller välj från galleriet.",
                ),
              );
            }
          }, timeoutDuration);

          // Verify file is ready before reading
          if (file.size === 0) {
            setTimeout(() => {
              if (isRejected) {
                return;
              }
              const checkTime = Date.now() - fileStartTime;
              if (file.size > 0) {
                reader.readAsDataURL(file);
              } else {
                isRejected = true;
                reject(
                  new Error("Filen är tom eller inte redo ännu. Försök igen."),
                );
              }
            }, 200);
          } else {
            const readStartTime = Date.now();
            reader.readAsDataURL(file);
          }
        };

        const handleLoaded = (url: string | null | undefined) => {
          const onloadTime = Date.now() - fileStartTime;
          if (readerTimeout) {
            clearTimeout(readerTimeout);
          }
          if (isRejected) {
            return;
          }

          if (
            !url ||
            typeof url !== "string" ||
            !(url.startsWith("data:") || (fileIsVideo && url.startsWith("blob:")))
          ) {
            isRejected = true;
            reject(new Error("Kunde inte läsa bilddata. Försök igen."));
            return;
          }

          // If testId exists and we're NOT in edit mode -> lazy upload immediately
          if (testId && !isEditMode) {
            setUploadingImages((prev) => new Set(prev).add(tempId));

            if (section === "exterior") {
              if (fileIsVideo) {
                setUploadingEngineVideo(true);
              } else if (isThumbnail) {
                setUploadingThumbnail(true);
              } else {
                setUploadingRegularExterior(true);
              }
            }

            const categoryMap: Record<
              string,
              "exterior" | "interior" | "damage" | "service" | "tires"
            > = {
              exterior: "exterior",
              interior: "interior",
              damage: "damage",
              serviceImages: "service",
              tires: "tires",
            };
            const category = categoryMap[section] || "exterior";
            const targetTireType =
              section === "tires" ? tireType || "primary" : undefined;

            CarTestService.addCarTestImage(
              testId,
              category,
              file,
              isThumbnail,
              targetTireType as "primary" | "secondary" | undefined,
              section === "damage" ? "" : undefined,
            )
              .then((uploadedImage) => {
                if (!uploadedImage || !uploadedImage.id || !uploadedImage.url) {
                  throw new Error(
                    "Ogiltigt svar från servern: bilden saknar ID eller URL",
                  );
                }

                let newImage: any;
                const uploadedType =
                  uploadedImage.type ||
                  (fileIsVideo ? file.type || "video/mp4" : file.type);

                if (section === "exterior") {
                  newImage = {
                    id: uploadedImage.id,
                    isThumbnail: isThumbnail,
                    url: uploadedImage.url,
                    type: uploadedType,
                    mediaId: uploadedImage.mediaId,
                    previewMediaId: uploadedImage.previewMediaId,
                    fullSizeMediaId: uploadedImage.fullSizeMediaId,
                  };
                } else if (section === "interior") {
                  newImage = {
                    id: uploadedImage.id,
                    url: uploadedImage.url,
                  };
                } else if (section === "damage") {
                  newImage = {
                    id: uploadedImage.id,
                    url: uploadedImage.url,
                    comment: "",
                  };
                } else if (section === "serviceImages") {
                  newImage = {
                    id: uploadedImage.id,
                    url: uploadedImage.url,
                  };
                } else if (section === "tires") {
                  newImage = {
                    id: uploadedImage.id,
                    url: uploadedImage.url,
                  };
                }

                // For eager uploads, we still need to save originalFile for full-size display in popup
                // Save it with the real image ID (not temp ID) so we can match it later
                addImageToState(
                  newImage,
                  {
                    file, // Compressed file for upload
                    originalFile, // Original file for full-size display in popup
                    imageId: uploadedImage.id, // Save real ID for matching (not temp ID)
                    section: section as any,
                    isThumbnail:
                      section === "exterior" ? isThumbnail : undefined,
                    tireType:
                      tireType || (section === "tires" ? "primary" : undefined),
                    comment: section === "damage" ? "" : undefined,
                  },
                  false, // Don't skip - we need to save originalFile even for eager uploads!
                );

                setTimeout(() => {
                  setCurrentImages((prevState) => {
                    let imageFound = false;
                    if (section === "exterior") {
                      imageFound =
                        prevState.exterior?.some(
                          (img: any) => img.id === uploadedImage.id,
                        ) || false;
                    } else if (section === "interior") {
                      imageFound =
                        prevState.interior?.some(
                          (img: any) => img.id === uploadedImage.id,
                        ) || false;
                    } else if (section === "damage") {
                      imageFound =
                        prevState.damage?.some(
                          (img: any) => img.id === uploadedImage.id,
                        ) || false;
                    } else if (section === "serviceImages") {
                      imageFound =
                        prevState.serviceImages?.some(
                          (img: any) => img.id === uploadedImage.id,
                        ) || false;
                    } else if (section === "tires" && targetTireType) {
                      imageFound =
                        prevState.tires?.[targetTireType]?.some(
                          (img: any) => img.id === uploadedImage.id,
                        ) || false;
                    }

                    if (!imageFound) {
                      if (section === "exterior") {
                        return {
                          ...prevState,
                          exterior: [...(prevState.exterior || []), newImage],
                        };
                      } else if (section === "interior") {
                        return {
                          ...prevState,
                          interior: [...(prevState.interior || []), newImage],
                        };
                      } else if (section === "damage") {
                        const newDamageArray = [
                          ...(prevState.damage || []),
                          newImage,
                        ];
                        const newIndex = newDamageArray.length - 1;
                        setExpandedComments((prevComments) => ({
                          ...prevComments,
                          [newIndex]: true,
                        }));
                        return {
                          ...prevState,
                          damage: newDamageArray,
                        };
                      } else if (section === "serviceImages") {
                        return {
                          ...prevState,
                          serviceImages: [
                            ...(prevState.serviceImages || []),
                            newImage,
                          ],
                        };
                      } else if (section === "tires") {
                        const targetTire = targetTireType || "primary";
                        return {
                          ...prevState,
                          tires: {
                            ...prevState.tires,
                            [targetTire]: [
                              ...(prevState.tires?.[targetTire] || []),
                              newImage,
                            ],
                          },
                        };
                      }
                    }
                    return prevState;
                  });
                }, 500);

                setUploadingImages((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(tempId);
                  if (section === "exterior" && newSet.size === 0) {
                    if (fileIsVideo) {
                      setUploadingEngineVideo(false);
                    } else if (isThumbnail) {
                      setUploadingThumbnail(false);
                    } else {
                      setUploadingRegularExterior(false);
                    }
                  }
                  return newSet;
                });

                const toasts =
                  (
                    listingPostLabels(lang).images as {
                      toasts?: Record<string, string>;
                    }
                  )?.toasts ?? {};
                toast.success(toasts.uploadSuccess ?? "Bild uppladdad");
                if (!isRejected) {
                  isResolved = true;
                  processedImagesRef.current.add(tempId);
                  processedImagesRef.current.add(uploadedImage.id);
                  resolve(uploadedImage.id);
                }
              })
              .catch((error) => {
                const errorMessage =
                  error instanceof Error ? error.message : "Okänt fel";
                const toasts =
                  (
                    listingPostLabels(lang).images as {
                      toasts?: Record<string, string>;
                    }
                  )?.toasts ?? {};
                const msg = (
                  toasts.uploadFailed ?? "Kunde inte ladda upp bilden: {error}"
                ).replace("{error}", errorMessage);
                toast.error(msg);

                setUploadingImages((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(tempId);
                  if (section === "exterior" && newSet.size === 0) {
                    if (fileIsVideo) {
                      setUploadingEngineVideo(false);
                    } else if (isThumbnail) {
                      setUploadingThumbnail(false);
                    } else {
                      setUploadingRegularExterior(false);
                    }
                  }
                  return newSet;
                });
                if (!isRejected) {
                  isRejected = true;
                  reject(error);
                }
              });
          } else {
            // No testId or edit mode - use normal flow
            if (section === "exterior") {
              const newImage: ExteriorImage = {
                id: tempId,
                isThumbnail: isThumbnail,
                url,
                type: fileIsVideo
                  ? file.type || "video/mp4"
                  : file.type || undefined,
              };
              addImageToState(newImage, {
                file, // Compressed file for upload
                originalFile, // Original file for full-size display in popup
                imageId: tempId, // Save image ID for matching
                section: "exterior",
                isThumbnail: isThumbnail,
              });
            } else if (section === "interior") {
              const newImage: FileObject = {
                id: tempId,
                url,
              };
              addImageToState(newImage, {
                file, // Compressed file for upload
                originalFile, // Original file for full-size display in popup
                imageId: tempId, // Save image ID for matching
                section: "interior",
              });
            } else if (section === "damage") {
              const newImage: DamageImage = {
                id: tempId,
                url,
                comment: "",
              };
              addImageToState(newImage, {
                file, // Compressed file for upload
                originalFile, // Original file for full-size display in popup
                imageId: tempId, // Save image ID for matching
                section: "damage",
                comment: "",
              });
            } else if (section === "serviceImages") {
              const newImage: FileObject = {
                id: tempId,
                url,
              };
              addImageToState(newImage, {
                file, // Compressed file for upload
                originalFile, // Original file for full-size display in popup
                imageId: tempId, // Save image ID for matching
                section: "serviceImages",
              });
            } else if (section === "tires") {
              const targetTireType = tireType || "primary";
              const newImage: FileObject = {
                id: tempId,
                url,
              };
              addImageToState(newImage, {
                file, // Compressed file for upload
                originalFile, // Original file for full-size display in popup
                imageId: tempId, // Save image ID for matching
                section: "tires",
                tireType: targetTireType,
              });
            }
            if (!isRejected) {
              const successTime = Date.now() - fileStartTime;
              isResolved = true;
              processedImagesRef.current.add(tempId);
              resolve(tempId);
            }
          }
        };

        reader.onload = (event) => {
          handleLoaded(event.target?.result as string);
        };

        reader.onerror = () => {
          const errorTime = Date.now() - fileStartTime;
          if (readerTimeout) {
            clearTimeout(readerTimeout);
          }
          if (!isRejected) {
            isRejected = true;
            const toasts =
              (
                listingPostLabels(lang).images as {
                  toasts?: Record<string, string>;
                }
              )?.toasts ?? {};
            reject(
              new Error(toasts.couldNotReadFile ?? "Kunde inte läsa filen"),
            );
          }
        };

        reader.onabort = () => {
          const abortTime = Date.now() - fileStartTime;
          if (readerTimeout) {
            clearTimeout(readerTimeout);
          }
          if (!isRejected) {
            isRejected = true;
            reject(
              new Error(
                "Bildläsning avbruten. Försök ta om fotot eller välj från galleriet.",
              ),
            );
          }
        };

        startReading();
      });
    },
    [
      lang,
      setCurrentImages,
      setAddedImages,
      setHasChanges,
      setExpandedComments,
      setUploadingImages,
      setUploadingThumbnail,
      setUploadingRegularExterior,
      setUploadingEngineVideo,
      isEditMode,
      testId,
      processedImagesRef,
    ],
  );

  const openFilePickerForFiles = useCallback(
    async (
      files: File[],
      section: keyof VehicleTestImages,
      isThumbnail: boolean = false,
      tireType?: "primary" | "secondary",
    ) => {
      if (files.length === 0) return;

      let currentCount = 0;
      let maxForSection = 10;

      if (section === "exterior") {
        const exterior = currentImages.exterior || [];
        // Photos only — engine video does not occupy a photo slot or become thumbnail
        const photoCount = exterior.filter(
          (img) => !isVideoMedia(img.type, img.url),
        ).length;
        currentCount = photoCount;
        maxForSection = EXTERIOR_MAX_REGULAR;
        const uploadingOnlyVideo =
          files.length === 1 && isVideoFile(files[0]);
        isThumbnail = uploadingOnlyVideo ? false : photoCount === 0;
      } else if (section === "interior") {
        currentCount = (currentImages.interior || []).length;
        maxForSection = 15;
      } else if (section === "damage") {
        currentCount = (currentImages.damage || []).length;
        maxForSection = 15;
      } else if (section === "serviceImages") {
        currentCount = (currentImages.serviceImages || []).length;
        maxForSection = 15;
      } else if (section === "tires" && tireType) {
        currentCount = (currentImages.tires?.[tireType] || []).length;
        maxForSection = 5;
      }

      const toasts =
        (listingPostLabels(lang).images as { toasts?: Record<string, string> })
          ?.toasts ?? {};
      if (!isThumbnail && files.length > 10) {
        toast.error(
          toasts.maxFiveAtOnce ??
            "Du kan bara ladda upp max 10 bilder åt gången",
        );
        return;
      }

      const maxNewFiles = Math.min(10, maxForSection - currentCount);
      if (maxNewFiles <= 0) {
        const msg = (
          toasts.maxReached ??
          "Max antal bilder ({max}) har redan nåtts för denna sektion"
        ).replace("{max}", String(maxForSection));
        toast.error(msg);
        return;
      }

      if (!isThumbnail && files.length > maxNewFiles) {
        const msg = (
          toasts.maxRemaining ??
          "Du kan bara ladda upp {remaining} bilder till (max {max} totalt, {current} redan uppladdade)"
        )
          .replace("{remaining}", String(maxNewFiles))
          .replace("{max}", String(maxForSection))
          .replace("{current}", String(currentCount));
        toast.error(msg);
        return;
      }

      const filesToProcess = files.slice(0, maxNewFiles);

      const tempIds = filesToProcess.map(
        (_, index) =>
          `temp-${Date.now()}-${index}-${Math.random()
            .toString(36)
            .slice(2, 11)}`,
      );
      tempIds.forEach((id) =>
        setUploadingImages((prev) => new Set(prev).add(id)),
      );

      const batchHasVideo = filesToProcess.some((f) => isVideoFile(f));
      if (section === "exterior") {
        if (batchHasVideo && filesToProcess.every((f) => isVideoFile(f))) {
          setUploadingEngineVideo(true);
        } else if (isThumbnail) {
          setUploadingThumbnail(true);
        } else {
          setUploadingRegularExterior(true);
        }
      }

      const batchTempIds = new Set(tempIds);

      const exteriorPhotoCountBefore =
        section === "exterior"
          ? (currentImages.exterior || []).filter(
              (img) => !isVideoMedia(img.type, img.url),
            ).length
          : 0;

      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        const tempId = tempIds[i];
        const fileIsVideo = isVideoFile(file);
        const photosBeforeThis = filesToProcess
          .slice(0, i)
          .filter((f) => !isVideoFile(f)).length;
        const fileIsThumbnail =
          section === "exterior" &&
          !fileIsVideo &&
          exteriorPhotoCountBefore + photosBeforeThis === 0;

        try {
          await new Promise((resolve) => setTimeout(resolve, 200));

          if (!file || file.size === 0) {
            const t =
              (
                listingPostLabels(lang).images as {
                  toasts?: Record<string, string>;
                }
              )?.toasts ?? {};
            throw new Error(
              t.fileEmptyOrInvalid ?? "Filen är tom eller ogiltig",
            );
          }

          await processImageFile(file, section, fileIsThumbnail, tireType);

          setUploadingImages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(tempId);
            batchTempIds.delete(tempId);
            if (section === "exterior" && batchTempIds.size === 0) {
              if (batchHasVideo && filesToProcess.every((f) => isVideoFile(f))) {
                setUploadingEngineVideo(false);
              } else if (isThumbnail) {
                setUploadingThumbnail(false);
              } else {
                setUploadingRegularExterior(false);
              }
            }
            return newSet;
          });
        } catch (error) {
          processedImagesRef.current.delete(tempId);

          setUploadingImages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(tempId);
            batchTempIds.delete(tempId);
            if (section === "exterior" && batchTempIds.size === 0) {
              if (batchHasVideo && filesToProcess.every((f) => isVideoFile(f))) {
                setUploadingEngineVideo(false);
              } else if (isThumbnail) {
                setUploadingThumbnail(false);
              } else {
                setUploadingRegularExterior(false);
              }
            }
            return newSet;
          });
        }
      }
    },
    [
      lang,
      currentImages,
      processImageFile,
      setUploadingImages,
      setUploadingThumbnail,
      setUploadingRegularExterior,
      setUploadingEngineVideo,
      processedImagesRef,
    ],
  );

  const openFilePicker = useCallback(
    (
      section: keyof VehicleTestImages,
      isThumbnail: boolean = false,
      tireType?: "primary" | "secondary",
      useCamera: boolean = false,
    ) => {


      if (isNative) {
        void (async () => {
          try {
            const file = useCamera
              ? await pickNativeCameraPhoto()
              : await pickNativeGalleryPhoto();
            if (!file) return;
            await openFilePickerForFiles(
              isThumbnail ? [file] : [file],
              section,
              isThumbnail,
              tireType,
            );
          } catch (error) {
            console.error("[openFilePicker] Native pick failed:", error);
          }
        })();
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.multiple = !isThumbnail;

      // Critical for Safari: hide but keep in DOM
      // Safari requires the input to be in the DOM tree for reliable change event firing
      input.style.position = "absolute";
      input.style.left = "-9999px";
      input.style.top = "-9999px";
      input.style.visibility = "hidden";
      input.style.opacity = "0";
      input.style.width = "1px";
      input.style.height = "1px";

      if (useCamera && (isMobile || isNative)) {
        input.setAttribute("capture", "environment");
      }

      // Cleanup function to remove input from DOM
      const cleanup = () => {
        if (input.parentNode) {
          input.parentNode.removeChild(input);
        }
      };

      input.onchange = async (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        if (files.length > 0) {
          let currentCount = 0;
          let maxForSection = 10;

          if (section === "exterior") {
            currentCount = (currentImages.exterior || []).length;
            maxForSection = EXTERIOR_MAX_REGULAR;
          } else if (section === "interior") {
            currentCount = (currentImages.interior || []).length;
            maxForSection = 15;
          } else if (section === "damage") {
            currentCount = (currentImages.damage || []).length;
            maxForSection = 15;
          } else if (section === "serviceImages") {
            currentCount = (currentImages.serviceImages || []).length;
            maxForSection = 15;
          } else if (section === "tires" && tireType) {
            currentCount = (currentImages.tires?.[tireType] || []).length;
            maxForSection = 5;
          }

          const t =
            (
              listingPostLabels(lang).images as {
                toasts?: Record<string, string>;
              }
            )?.toasts ?? {};
          if (files.length > 10) {
            toast.error(
              t.maxFiveAtOnce ??
                "Du kan bara ladda upp max 10 bilder åt gången",
            );
            input.value = "";
            cleanup();
            return;
          }

          const maxNewFiles = Math.min(10, maxForSection - currentCount);
          if (maxNewFiles <= 0) {
            const msg = (
              t.maxReached ??
              "Max antal bilder ({max}) har redan nåtts för denna sektion"
            ).replace("{max}", String(maxForSection));
            toast.error(msg);
            input.value = "";
            cleanup();
            return;
          }

          if (files.length > maxNewFiles) {
            const msg = (
              t.maxRemaining ??
              "Du kan bara ladda upp {remaining} bilder till (max {max} totalt, {current} redan uppladdade)"
            )
              .replace("{remaining}", String(maxNewFiles))
              .replace("{max}", String(maxForSection))
              .replace("{current}", String(currentCount));
            toast.error(msg);
            input.value = "";
            cleanup();
            return;
          }

          const filesToProcess = files.slice(0, maxNewFiles);

          const tempIds = filesToProcess.map(
            (_, index) =>
              `temp-${Date.now()}-${index}-${Math.random()
                .toString(36)
                .slice(2, 11)}`,
          );
          tempIds.forEach((id) =>
            setUploadingImages((prev) => new Set(prev).add(id)),
          );

          if (section === "exterior") {
            setUploadingRegularExterior(true);
          }

          const batchTempIds = new Set(tempIds);
          const exteriorCountBefore =
            section === "exterior" ? currentCount : 0;

          for (let i = 0; i < filesToProcess.length; i++) {
            const file = filesToProcess[i];
            const tempId = tempIds[i];
            const fileIsThumbnail =
              section === "exterior" &&
              exteriorCountBefore + i === 0 &&
              !isVideoFile(file);

            try {
              await new Promise((resolve) => setTimeout(resolve, 200));

              if (!file || file.size === 0) {
                const t2 =
                  (
                    listingPostLabels(lang).images as {
                      toasts?: Record<string, string>;
                    }
                  )?.toasts ?? {};
                throw new Error(
                  t2.fileEmptyOrInvalid ?? "Filen är tom eller ogiltig",
                );
              }
              await processImageFile(file, section, fileIsThumbnail, tireType);

              if ((isIOS() || isAndroid()) && useCamera) {
                // Wait longer for eager uploads to complete (when testId exists and !isEditMode)
                // The upload happens asynchronously, so we need to wait for it to finish
                const waitTime = testId && !isEditMode ? 2000 : 800;
                await new Promise((resolve) => setTimeout(resolve, waitTime));

                // Check both tempId and any real IDs in processedImagesRef
                const imageFoundInRef = processedImagesRef.current.has(tempId);

                // Also check state for both tempId and real IDs (for eager uploads)
                let imageInState = false;
                let imageIdInState: string | undefined = undefined;
                setCurrentImages((prev) => {
                  if (section === "exterior") {
                    const found = prev.exterior?.find(
                      (img: any) =>
                        img.id === tempId ||
                        img.id?.startsWith("temp-") ||
                        (!img.id?.startsWith("temp-") &&
                          processedImagesRef.current.has(img.id)),
                    );
                    imageInState = !!found;
                    imageIdInState = found?.id;
                  } else if (section === "interior") {
                    const found = prev.interior?.find(
                      (img: any) =>
                        img.id === tempId ||
                        img.id?.startsWith("temp-") ||
                        (!img.id?.startsWith("temp-") &&
                          processedImagesRef.current.has(img.id)),
                    );
                    imageInState = !!found;
                    imageIdInState = found?.id;
                  } else if (section === "damage") {
                    const found = prev.damage?.find(
                      (img: any) =>
                        img.id === tempId ||
                        img.id?.startsWith("temp-") ||
                        (!img.id?.startsWith("temp-") &&
                          processedImagesRef.current.has(img.id)),
                    );
                    imageInState = !!found;
                    imageIdInState = found?.id;
                  } else if (section === "serviceImages") {
                    const found = prev.serviceImages?.find(
                      (img: any) =>
                        img.id === tempId ||
                        img.id?.startsWith("temp-") ||
                        (!img.id?.startsWith("temp-") &&
                          processedImagesRef.current.has(img.id)),
                    );
                    imageInState = !!found;
                    imageIdInState = found?.id;
                  } else if (section === "tires") {
                    const targetTire = tireType || "primary";
                    const found = prev.tires?.[targetTire]?.find(
                      (img: any) =>
                        img.id === tempId ||
                        img.id?.startsWith("temp-") ||
                        (!img.id?.startsWith("temp-") &&
                          processedImagesRef.current.has(img.id)),
                    );
                    imageInState = !!found;
                    imageIdInState = found?.id;
                  }
                  return prev;
                });

                // Image is valid if found in ref OR in state (for eager uploads, real ID might be in state)
                const imageFound = imageFoundInRef || imageInState;

                if (!imageFound) {
                  processedImagesRef.current.delete(tempId);
                  if (imageIdInState) {
                    processedImagesRef.current.delete(imageIdInState);
                  }
                  throw new Error(
                    "Bilden kunde inte verifieras i systemet. Försök ta om fotot eller välj från galleriet.",
                  );
                } else {
                  // Ensure both tempId and any real ID are in ref for future checks
                  if (tempId) {
                    processedImagesRef.current.add(tempId);
                  }
                  if (imageIdInState && !imageIdInState.startsWith("temp-")) {
                    processedImagesRef.current.add(imageIdInState);
                  }
                }
              }

              setUploadingImages((prev) => {
                const newSet = new Set(prev);
                newSet.delete(tempId);
                batchTempIds.delete(tempId);
                if (section === "exterior" && batchTempIds.size === 0) {
                  if (isThumbnail) {
                    setUploadingThumbnail(false);
                  } else {
                    setUploadingRegularExterior(false);
                  }
                }
                return newSet;
              });
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Kunde inte ladda upp bilden. Försök igen.",
              );

              processedImagesRef.current.delete(tempId);

              setUploadingImages((prev) => {
                const newSet = new Set(prev);
                newSet.delete(tempId);
                batchTempIds.delete(tempId);
                if (section === "exterior" && batchTempIds.size === 0) {
                  if (isThumbnail) {
                    setUploadingThumbnail(false);
                  } else {
                    setUploadingRegularExterior(false);
                  }
                }
                return newSet;
              });

              // Remove any partial image from state
              if (section === "exterior") {
                setCurrentImages((prev) => ({
                  ...prev,
                  exterior: (prev.exterior || []).filter(
                    (img: any) =>
                      img.id !== tempId &&
                      !img.id?.startsWith(tempId.split("-")[0] + "-"),
                  ),
                }));
              } else if (section === "interior") {
                setCurrentImages((prev) => ({
                  ...prev,
                  interior: (prev.interior || []).filter(
                    (img: any) =>
                      img.id !== tempId &&
                      !img.id?.startsWith(tempId.split("-")[0] + "-"),
                  ),
                }));
              } else if (section === "damage") {
                setCurrentImages((prev) => ({
                  ...prev,
                  damage: (prev.damage || []).filter(
                    (img: any) =>
                      img.id !== tempId &&
                      !img.id?.startsWith(tempId.split("-")[0] + "-"),
                  ),
                }));
              } else if (section === "serviceImages") {
                setCurrentImages((prev) => ({
                  ...prev,
                  serviceImages: (prev.serviceImages || []).filter(
                    (img: any) =>
                      img.id !== tempId &&
                      !img.id?.startsWith(tempId.split("-")[0] + "-"),
                  ),
                }));
              } else if (section === "tires") {
                const targetTireType = tireType || "primary";
                setCurrentImages((prev) => ({
                  ...prev,
                  tires: {
                    ...prev.tires,
                    [targetTireType]: (
                      prev.tires?.[targetTireType] || []
                    ).filter(
                      (img: any) =>
                        img.id !== tempId &&
                        !img.id?.startsWith(tempId.split("-")[0] + "-"),
                    ),
                  },
                }));
              }

              setAddedImages((prev) =>
                prev.filter(
                  (added) =>
                    added.file !== file &&
                    !(
                      added.section === section &&
                      added.isThumbnail === isThumbnail &&
                      added.tireType === tireType
                    ),
                ),
              );
            }
          }
        }

        input.value = "";
        // Clean up the input element after processing
        cleanup();
      };

      // Handle cancel (no file selected) - Safari fires focus on window when picker closes
      const handleCancel = () => {
        window.removeEventListener("focus", handleCancel);
        // Small delay to allow onchange to fire first if file was selected
        setTimeout(() => {
          cleanup();
        }, 300);
      };
      window.addEventListener("focus", handleCancel, { once: true });

      // Append to DOM before clicking - critical for Safari
      document.body.appendChild(input);

      // Use requestAnimationFrame to ensure DOM render before clicking
      // Double RAF ensures the browser has completed a full paint cycle
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          input.click();
        });
      });
    },
    [
      lang,
      currentImages,
      isMobile,
      isNative,
      openFilePickerForFiles,
      processImageFile,
      setCurrentImages,
      setAddedImages,
      setUploadingImages,
      setUploadingThumbnail,
      setUploadingRegularExterior,
      setUploadingEngineVideo,
      processedImagesRef,
    ],
  );

  return {
    processImageFile,
    openFilePickerForFiles,
    openFilePicker,
  };
};
